<script>
import config from "@/config";
import { S3Client, ListObjectsV2Command, GetObjectCommand } from "@aws-sdk/client-s3";
import _ from "lodash";
import DepositList from "@/components/DepositList.vue";
import DepositsSidebar from "@/components/DepositsSidebar.vue";

export default {
  components: { DepositList, DepositsSidebar },
  data() {
    return { deposits: [], loading: false }
  },
  computed: {
    sortedDeposits() {
      return _.orderBy(this.deposits, ['status', 'createdDate'], ['desc', 'desc'])
    }
  },
  async created() {
    await this.getData();
  },
  methods: {
    // read custom:identityid from the signed-in user's attributes (via your cognito plugin)
    async getCustomIdentityId() {
      return new Promise((resolve) => {
        const user = this.$cognitoAuth?.getCurrentUser?.();
        if (!user) return resolve(null);
        user.getUserAttributes((err, attrs) => {
          if (err || !attrs) return resolve(null);
          // handle both CognitoUserAttribute objects or plain {Name, Value}
          const find = (a) =>
            (typeof a.getName === 'function' ? a.getName() : a.Name) === 'custom:identityid';
          const match = attrs.find(find);
          if (!match) return resolve(null);
          const val = typeof match.getValue === 'function' ? match.getValue() : match.Value;
          resolve(val || null);
        });
      });
    },

    async streamToString(body) {
      if (body && typeof body.getReader === 'function') {
        const reader = body.getReader();
        const decoder = new TextDecoder('utf-8');
        let out = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          out += decoder.decode(value, { stream: true });
        }
        out += decoder.decode();
        return out;
      }
      if (body?.text) return body.text();                 // Blob
      if (body?.transformToByteArray) {                   // R2 streams
        const bytes = await body.transformToByteArray();
        return new TextDecoder('utf-8').decode(bytes);
      }
      return '';
    },

    async listPrefix(client, prefix) {
      const listed = await client.send(new ListObjectsV2Command({
        Bucket: config.bucketName,
        Prefix: prefix,
      }));
      const objs = (listed.Contents || []).filter(o => o.Key);
      // fetch and parse all metadata objects
      const items = await Promise.all(
        objs.map(async (o) => {
          const Key = o.Key;
          try {
            const out = await client.send(new GetObjectCommand({ Bucket: config.bucketName, Key }));
            const text = await this.streamToString(out.Body);
            if (!text) return null;
            const obj = JSON.parse(text);

            obj.key = Key;

            // ➕ carry S3 last-modified timestamp
            obj._s3LastModified = o.LastModified
              ? new Date(o.LastModified).toISOString()
              : null;

            if (obj.createdDate) obj.createdDate = new Date(obj.createdDate).toISOString();
            return obj;
          } catch (e) {
            console.warn('getObject failed for', Key, e);
            return null;
          }
        })
      );
      return items.filter(Boolean);
    },

    async getData() {
      this.loading = true;
      try {
        // More robust: use the plugin’s helper (falls back and refreshes correctly)
        const creds = await this.$cognitoAuth.getAwsCredentials();
        if (!creds || !creds.identityId) {
          console.error('No AWS credentials/identityId; are you logged in?');
          this.deposits = [];
          return;
        }

        const client = new S3Client({
          region: config.region,
          credentials: {
            accessKeyId: creds.accessKeyId,
            secretAccessKey: creds.secretAccessKey,
            sessionToken: creds.sessionToken,
        },
        // important for buckets with dots in the name over HTTPS
          forcePathStyle: true,
        });

        console.log('S3 list params', {
          origin: window.location.origin,
          bucket: config.bucketName,
          identityId: creds.identityId,
        });

        const currentId = creds.identityId;
        const storedId  = await this.getCustomIdentityId();

        const prefixes = [];
        if (currentId) prefixes.push(`private/${currentId}/deposits/`);
        if (storedId && storedId !== currentId) prefixes.push(`private/${storedId}/deposits/`);

        // list all candidate prefixes, merge and de-dup by deposit id
        const all = (await Promise.all(prefixes.map(p => this.listPrefix(client, p))))
          .flat()
          .filter(Boolean);

        const byId = new Map();

        for (const d of all) {
          if (!d?.id) continue;
          const prev = byId.get(d.id);

          if (!prev) {
            byId.set(d.id, d);
            continue;
          }

          // compare S3 last-modified timestamps
          const prevLm = prev._s3LastModified ? Date.parse(prev._s3LastModified) : 0;
          const curLm  = d._s3LastModified ? Date.parse(d._s3LastModified) : 0;

          // ➕ keep the newest copy
          if (curLm >= prevLm) {
            byId.set(d.id, d);
          }
        }

        this.deposits = Array.from(byId.values());

      } catch (err) {
        console.error('DepositHome getData error', err);
        this.deposits = [];
      }

      finally {
        this.loading = false;   // <-- ensure spinner hides on both paths
      }
    },
  }
}
</script>

<template>
  <v-container>
    <DepositList
      :deposits="sortedDeposits"
      :loading="loading"
      @refresh="getData"
    />
    <DepositsSidebar :deposits="sortedDeposits" />
  </v-container>
</template>
